import { useEffect, useRef, useState } from "react";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import Dialog, { type Ref } from "@/components/Dialog";
import SegmentedButton from "@/components/SegmentedButton";
import TextField from "@/components/TextField";
import type { CommitteeRow, CommitteeStatus } from "../types";

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" }
];

export type CommitteeDialogMode = "add" | "edit";

export type CommitteeDialogProps = {
  open: boolean;
  mode: CommitteeDialogMode;
  initialCommittee?: CommitteeRow;
  saving?: boolean;
  onClose(): void;
  onSave(input: CommitteeDialogSaveInput): void;
};

export type CommitteeDialogSaveInput =
  | { mode: "add"; name: string; status: CommitteeStatus; core: boolean }
  | { mode: "edit"; committeeId: string; name: string; status: CommitteeStatus; core: boolean };

type FormState = {
  name: string;
  status: CommitteeStatus;
  core: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  status: "ACTIVE",
  core: false
};

export default function CommitteeDialog({
  open,
  mode,
  initialCommittee,
  saving = false,
  onClose,
  onSave
}: CommitteeDialogProps) {
  const dialogRef = useRef<Ref>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [headlineMode, setHeadlineMode] = useState<CommitteeDialogMode>(mode);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setHeadlineMode(mode);
    }
    wasOpenRef.current = open;
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialCommittee) {
      setForm({
        name: initialCommittee.name,
        status: initialCommittee.status,
        core: initialCommittee.core
      });
      return;
    }

    setForm(EMPTY_FORM);
  }, [open, mode, initialCommittee]);

  const isValid = form.name.trim().length > 0;
  const headline = headlineMode === "add" ? "Add Committee" : "Edit Committee";

  const handleRequestClose = () => {
    if (saving) return;
    dialogRef.current?.close();
  };

  const handleSave = () => {
    if (!isValid || saving) return;

    const payload = {
      name: form.name.trim(),
      status: form.status,
      core: form.core
    };

    if (mode === "add") {
      onSave({ mode: "add", ...payload });
      return;
    }

    if (!initialCommittee) return;

    onSave({
      mode: "edit",
      committeeId: initialCommittee.id,
      ...payload
    });
  };

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      headline={headline}
      onClosed={onClose}
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={handleRequestClose} testId="committee-dialog-cancel" disabled={saving}>
            Cancel
          </Button>
          <Button variant="filled" onClick={handleSave} disabled={!isValid || saving} testId="committee-dialog-save">
            Save
          </Button>
        </>
      }
    >
      <div className="flex min-w-[360px] flex-col gap-6 pt-2">
        <TextField
          label="Committee Name"
          value={form.name}
          onChangeText={(value) => setForm((current) => ({ ...current, name: value }))}
          required
        />
        <Checkbox
          name="committee-is-core"
          label="Core Committee"
          checked={form.core}
          onChange={(checked) => setForm((current) => ({ ...current, core: checked }))}
        />
        <div className="flex flex-col gap-2" data-testid="committee-status-toggle">
          <span className="text-label-large text-sys-brand-on-surface">Status *</span>
          <SegmentedButton
            options={STATUS_OPTIONS}
            value={form.status}
            onChange={(value) => {
              if (value) {
                setForm((current) => ({ ...current, status: value as CommitteeStatus }));
              }
            }}
            fullWidth
            showCheckmark
          />
        </div>
      </div>
    </Dialog>
  );
}
